import React from 'react';
import styles from '../constants/styles';
import RBISwitch from './RBISwitch';
import RBISelectUncontrolled from './RBISelectUncontrolled';
import { Divider, FormLabel, Typography, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { textToSnakeCase } from '../../../utils/utils';
import { getIsValidatedOnSubmit, getIsHandledByMultivalueJson, getReferentAttributeCode } from '../../../utils/multivalueJsonAttributes';
import { RootStateOrAny, useSelector } from 'react-redux';

const AttributesForm = ({
  attributes,
  title,
  prefix = 'att',
  disabled = false,
}) => {
  const classes = styles();
  const { t } = useTranslation();
  const { watch } = useFormContext();
  let categories: string[] = [];

  const { brand, Region, CountryId  } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );

  attributes.forEach(attribute => {
    if (categories.includes(attribute.categoryCode)) {
      return;
    }

    categories.push(attribute.categoryCode);
  });
  return (
    <div className={classes.attributesForm}>
      <Typography variant="subtitle1">{title}</Typography>
      {categories &&
        categories.map(category => {
          const relevantAttributes = attributes.filter(({ type, categoryCode }) => !!type && category === categoryCode);
          return (
            <div className={classes.sectionDivider}>
              <FormLabel
                component="legend"
                className={classes.sectionDividerTitle}
              >
                {t(`restaurant.categoriesAndServices.${category}`)}
              </FormLabel>
              <Divider />

              <div className={classes.flexSpaceBetween}>
                {relevantAttributes.map(attribute => {
                  if (skipForPosType({ attribute, brand, Region })) return null;
                  if (skipForThNaOnlyAttribute({ attribute, brand, Region })) return null;
                  if (skipForStickyLabelPrinter({ attribute, brand, CountryId })) return null;
                  if (getIsHandledByMultivalueJson(attribute.code)) return null;

                  const isReadOnly = !attribute.modes.includes('write') || disabled;

                  let parentValue = null;
                  let parentName = '';
                  if (attribute.parent) {
                    parentName = generateName(
                      attributes.find(item => item.code === attribute.parent),
                      prefix,
                    );
                    parentValue = watch(parentName);
                    if (parentValue !== undefined && !parentValue) {
                      attribute.value = null;
                    }
                  }
                  const renderElement = !parentName || (parentName && parentValue);

                  if (!renderElement) return null;

                  const name = generateName(attribute, prefix);

                  if (attribute.type === 'toggle') {
                    return (
                      <ToggleAttribute
                        classes={classes}
                        attribute={attribute}
                        name={name}
                        isReadOnly={isReadOnly}
                        t={t}
                      />
                    );
                  }

                  if (!attribute.multivalueJson) {
                    return (
                      <SingleEnumAttribute
                        classes={classes}
                        attribute={attribute}
                        name={name}
                        isReadOnly={isReadOnly}
                        required={!!parentValue}
                        t={t}
                      />
                    )
                  }

                  return (
                    <MultiEnumAttribute
                      classes={classes}
                      attribute={attribute}
                      attributes={attributes}
                      isReadOnly={isReadOnly}
                      prefix={prefix}
                      parentValue={parentValue}
                      t={t}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default AttributesForm;

function ToggleAttribute({ classes, attribute, isReadOnly, name, t }) {
  return (
    <div className={classes.attributeItem}>
      <RBISwitch
        name={name}
        label={t(
          `restaurant.attributes_and_services.${attribute.code}`,
          attribute.name,
        )}
        defaultValue={attribute.value}
        disabled={isReadOnly}
      />
    </div>
  );
}

function SingleEnumAttribute({ classes, attribute, isReadOnly, name, t, labelCode = undefined, required }) {
  const getOption = (value) => {
    let name;
    try {
      name = t(`restaurant.attributes_and_services.${textToSnakeCase(value.toString() || '')}`, value)
    } catch (e) {
      name = t('restaurant.attributes_and_services.UNKNOWN', null);
    }
  
    return { value, name };
  };

  const readOnlyOptions = [getOption(attribute.value)];
  const editableOptions = attribute.enumValues.map(value => getOption(value));

  const options = isReadOnly ? readOnlyOptions : editableOptions;

  const label = t(`restaurant.attributes_and_services.${labelCode || attribute.code}`, attribute.name); 

  return (
    <div className={classes.attributeItem}>
      <RBISelectUncontrolled
        name={name}
        label={label}
        options={options}
        defaultValue={attribute.value}
        disabled={isReadOnly}
        required={!isReadOnly && !!required}
      />
    </div>
  );
}

function MultiEnumAttribute({ classes, attribute, isReadOnly, prefix, t, attributes, parentValue }) {
  const { multivalueJson } = attribute;

  const referentAttributeCode = getReferentAttributeCode(attribute.code);
  const referentAttribute = attributes.find(({ code }) => code === referentAttributeCode);

  if (!referentAttribute) return null;

  const isValidatedOnSubmit = getIsValidatedOnSubmit(referentAttributeCode);
  const multivalues = JSON.parse(multivalueJson);
  const indexedMultiValues = multivalues.reduce((acc, [sourceValue, referentValue]) => ({ ...acc, [sourceValue]: referentValue === 0 ? null : referentValue }), {});

  return (
    <Grid container>
      {
        attribute.enumValues.map((enumValue, idx) => (
          <MultiEnumPair
            indexedMultiValues={indexedMultiValues}
            enumValue={enumValue}
            attribute={attribute}
            idx={idx}
            referentAttribute={referentAttribute}
            classes={classes}
            prefix={prefix}
            isReadOnly={isReadOnly}
            t={t}
            required={!isValidatedOnSubmit && parentValue}
          />
        ))
      }
    </Grid>
  );
}

function MultiEnumPair({ indexedMultiValues, enumValue, attribute, idx, referentAttribute, classes, prefix, isReadOnly, t, required }) {
  const referentValue = indexedMultiValues[enumValue];
  const adjustedAttribute = { ...attribute, value: enumValue.toString(), code: `${attribute.code}_${idx}` };
  const adjustedReferent = { ...referentAttribute, value: ([null, undefined].includes(referentValue) ? referentValue : referentValue.toString()), code: `${referentAttribute.code}_${idx}` };

  return (
    <>
      <SingleEnumAttribute
        classes={classes}
        attribute={adjustedAttribute}
        isReadOnly={true}
        name={generateName(adjustedAttribute, prefix)}
        labelCode={attribute.code}
        t={t}
        required={false}
      />
      <SingleEnumAttribute
        classes={classes}
        attribute={adjustedReferent}
        isReadOnly={isReadOnly}
        name={generateName(adjustedReferent, prefix)}
        labelCode={referentAttribute.code}
        t={t}
        required={required}
      />
    </>
  );
}

function skipForPosType({ attribute, brand, Region }) {
  // PBI 18640 - TH wanted posType to be removed from RAM as they don't want FZs to change this value
  // the POS type always needs to be "PRODCUT_NUMBER". changing this would break the mobile app
  if (attribute.code !== 'posType') return false;
  return [
    (brand === 'TH' && (Region === 'LAC' || Region === 'APAC')),
    (brand === 'BK' && (Region === 'NA' || Region === 'LAC')),
    (brand === 'PLK' && (Region === 'NA' || Region === 'LAC')),
  ].some(Boolean);
}

function skipForThNaOnlyAttribute({ attribute, brand, Region }) {
  if (!['integratedFreshBrew', 'avftVeggieWash', 'delivery'].includes(attribute.code)) return false;
  return (brand !== 'TH' || Region !== 'NA');
}

function skipForStickyLabelPrinter({ attribute, brand, CountryId }) {
  // PBI -19614 - Adding new equipment to RAM: Sticky Label Printer
  // Enable the stickyLabelPrinter only for BK - CAN/USA
  if (attribute.code !== 'stickyLabelPrinter') return false;
  if (brand !== 'BK') return false;
  return (![36, 212].includes(CountryId));
}

function generateName(attribute, prefix) {
  if (typeof attribute === 'undefined') return '';

  const symbol = !attribute.modes.includes('write') ? '+' : '-';

  return `${prefix}${symbol}${attribute.code}`;
}